import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Label as LabelIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { tagService } from '../services/api';
import TagChip from './TagChip';

const TagManager = ({ open, onClose, onTagChange }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingTag, setEditingTag] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form states
  const [tagName, setTagName] = useState('');
  const [tagDescription, setTagDescription] = useState('');
  const [tagColor, setTagColor] = useState('#1976d2');

  useEffect(() => {
    if (open) {
      loadTags();
    }
  }, [open]);

  const loadTags = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await tagService.getAllTags();
      setTags(response.data || []);
    } catch (err) {
      setError('Erro ao carregar tags: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTag = async () => {
    if (!tagName.trim()) {
      setError('Nome da tag é obrigatório');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const tagData = {
        name: tagName.trim(),
        description: tagDescription.trim() || undefined,
        color: tagColor,
      };

      const response = await tagService.createTag(tagData);
      const newTag = response.data;
      
      setTags(prev => [...prev, newTag]);
      setSuccess('Tag criada com sucesso!');
      resetForm();
      
      if (onTagChange) {
        onTagChange();
      }
    } catch (err) {
      setError('Erro ao criar tag: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTag = async () => {
    if (!tagName.trim() || !editingTag) {
      setError('Nome da tag é obrigatório');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const tagData = {
        name: tagName.trim(),
        description: tagDescription.trim() || undefined,
        color: tagColor,
      };

      const response = await tagService.updateTag(editingTag.id, tagData);
      const updatedTag = response.data;
      
      setTags(prev => prev.map(tag => 
        tag.id === editingTag.id ? updatedTag : tag
      ));
      setSuccess('Tag atualizada com sucesso!');
      resetForm();
      
      if (onTagChange) {
        onTagChange();
      }
    } catch (err) {
      setError('Erro ao atualizar tag: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta tag? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await tagService.deleteTag(tagId);
      setTags(prev => prev.filter(tag => tag.id !== tagId));
      setSuccess('Tag excluída com sucesso!');
      
      if (onTagChange) {
        onTagChange();
      }
    } catch (err) {
      setError('Erro ao excluir tag: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEditTag = (tag) => {
    setEditingTag(tag);
    setTagName(tag.name);
    setTagDescription(tag.description || '');
    setTagColor(tag.color);
  };

  const resetForm = () => {
    setEditingTag(null);
    setTagName('');
    setTagDescription('');
    setTagColor('#1976d2');
  };

  const handleClose = () => {
    resetForm();
    setError('');
    setSuccess('');
    onClose();
  };

  const filteredTags = tags.filter(tag =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (tag.description && tag.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const predefinedColors = [
    '#1976d2', '#388e3c', '#f57c00', '#d32f2f',
    '#7b1fa2', '#303f9f', '#0288d1', '#00796b',
    '#689f38', '#fbc02d', '#ff8f00', '#f57c00',
    '#e64a19', '#5d4037', '#616161', '#455a64'
  ];

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <LabelIcon />
          Gerenciar Tags
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Form para criar/editar tag */}
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {editingTag ? 'Editar Tag' : 'Nova Tag'}
          </Typography>
          
          <TextField
            label="Nome da Tag"
            value={tagName}
            onChange={(e) => setTagName(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            disabled={loading}
            required
          />
          
          <TextField
            label="Descrição (opcional)"
            value={tagDescription}
            onChange={(e) => setTagDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mb: 2 }}
            disabled={loading}
          />
          
          <Typography variant="body2" gutterBottom>
            Cor da Tag:
          </Typography>
          
          <Box display="flex" gap={1} mb={2} flexWrap="wrap">
            {predefinedColors.map((color) => (
              <Box
                key={color}
                onClick={() => setTagColor(color)}
                sx={{
                  width: 30,
                  height: 30,
                  backgroundColor: color,
                  border: tagColor === color ? '3px solid #000' : '1px solid #ccc',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              />
            ))}
          </Box>
          
          <TextField
            label="Cor Personalizada"
            type="color"
            value={tagColor}
            onChange={(e) => setTagColor(e.target.value)}
            sx={{ mb: 2, width: '120px' }}
            disabled={loading}
          />
          
          {tagName && (
            <Box mb={2}>
              <Typography variant="body2" gutterBottom>
                Preview:
              </Typography>
              <TagChip 
                tag={{ 
                  name: tagName, 
                  description: tagDescription, 
                  color: tagColor 
                }} 
              />
            </Box>
          )}
          
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={editingTag ? <EditIcon /> : <AddIcon />}
              onClick={editingTag ? handleUpdateTag : handleCreateTag}
              disabled={loading || !tagName.trim()}
            >
              {editingTag ? 'Atualizar' : 'Criar'} Tag
            </Button>
            
            {editingTag && (
              <Button
                variant="outlined"
                onClick={resetForm}
                disabled={loading}
              >
                Cancelar
              </Button>
            )}
          </Box>
        </Paper>

        {/* Lista de tags existentes */}
        <Typography variant="h6" gutterBottom>
          Tags Existentes ({tags.length})
        </Typography>
        
        <TextField
          placeholder="Buscar tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        )}
        
        {!loading && filteredTags.length === 0 && (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            {searchTerm ? 'Nenhuma tag encontrada para a busca.' : 'Nenhuma tag encontrada. Crie sua primeira tag!'}
          </Typography>
        )}
        
        {!loading && filteredTags.length > 0 && (
          <List>
            {filteredTags.map((tag, index) => (
              <React.Fragment key={tag.id}>
                <ListItem>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <TagChip tag={tag} />
                      </Box>
                    }
                    secondary={tag.description || 'Sem descrição'}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      onClick={() => handleEditTag(tag)}
                      disabled={loading}
                      size="small"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDeleteTag(tag.id)}
                      disabled={loading}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index < filteredTags.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TagManager;